import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUpRight, FiMonitor, FiSearch } from 'react-icons/fi';
import { getPublicDisplayBranches } from '../api/branchDisplayApi';
import '../components/branchDisplay/branchDisplay.css';
import './DisplayDirectoryPage.css';

const normalize = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function DisplayDirectoryPage() {
  const [branches, setBranches] = useState([]);
  const [status, setStatus] = useState('loading');
  const [search, setSearch] = useState('');
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    getPublicDisplayBranches({ signal: controller.signal }).then((data) => {
      if (controller.signal.aborted) return;
      if (!Array.isArray(data?.branches)) throw new Error('Invalid directory response');
      setBranches(data.branches);
      setStatus('ready');
    }).catch(() => { if (!controller.signal.aborted) setStatus('error'); });
    window.addEventListener('online', retry);
    const visible = () => { if (document.visibilityState === 'visible') retry(); };
    document.addEventListener('visibilitychange', visible);
    const timer = window.setInterval(retry, 60000);
    return () => {
      controller.abort();
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', visible);
      window.clearInterval(timer);
    };
  }, [attempt, retry]);
  useEffect(() => {
    const title = document.title;
    document.title = 'Pantallas de destacados';
    return () => { document.title = title; };
  }, []);
  const visible = branches.filter((branch) => normalize(`${branch.branchName} ${branch.companyName}`).includes(normalize(search.trim())));
  return <main className="display-directory">
    <header className="display-directory-header"><span><FiMonitor aria-hidden="true" /> Pantallas de destacados</span><Link to="/">Intranet <FiArrowUpRight aria-hidden="true" /></Link></header>
    <div className="display-directory-inner">
      <section className="display-directory-intro">
        <p className="display-directory-eyebrow">Nuestras sucursales · En pantalla</p>
        <h1>Lo mejor de hoy.<br /><span>En primera fila.</span></h1>
        <p>Elige una sucursal y descubre sus destacados en pantalla.</p>
        <div className="display-directory-tip"><FiMonitor aria-hidden="true" /><span>Ideal para tu TV o proyector. Abre la presentación y pulsa <strong>Pantalla completa</strong>.</span></div>
      </section>
      <section aria-labelledby="display-branches-heading" className="display-directory-branches">
        <div className="display-directory-section-heading"><h2 id="display-branches-heading">Elige tu sucursal</h2><span>{branches.length} disponibles</span></div>
        <label className="display-directory-search"><FiSearch aria-hidden="true" /><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar sucursal o empresa" aria-label="Buscar sucursal o empresa" /></label>
        {status === 'loading' && branches.length === 0 && <p role="status">Cargando sucursales…</p>}
        {status === 'error' && <div role="alert" className="display-directory-message"><p>No pudimos actualizar las sucursales.</p><button type="button" onClick={retry}>Intentar nuevamente</button></div>}
        {status === 'ready' && branches.length === 0 && <div className="display-directory-message"><FiMonitor size={32} /><h3>Pronto, más destacados.</h3><p>Las sucursales aparecerán aquí cuando publiquen su pantalla.</p></div>}
        {branches.length > 0 && visible.length === 0 && <p role="status">No encontramos sucursales con ese nombre.</p>}
        <div className="display-directory-grid">{visible.flatMap((branch) => (branch.screens || [{ id: null, name: 'Principal' }]).map((screen) => <Link className="display-directory-card" key={`${branch.branchId}:${screen.id}`} to={`/branches/${branch.branchId}/display${screen.id ? `/${screen.id}` : ''}`}>
          <span className="display-directory-card-top"><FiMonitor aria-hidden="true" /><span>Ver destacados <FiArrowUpRight aria-hidden="true" /></span></span>
          <span className="display-directory-company">{branch.companyName}</span><h3>{branch.branchName}</h3><p className="mb-4 font-semibold">{screen.name}</p>
          <span className="display-directory-card-bottom">Abrir presentación <span aria-hidden="true">→</span></span>
        </Link>))}</div>
      </section>
    </div>
    <footer className="display-directory-footer">Una selección especial, en cada sucursal.</footer>
  </main>;
}
